import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HttpService } from 'src/app/http.service';

// Seamless player credit-limit (2026-07-16).
// Tenant grants a player credit (credited straight into the wallet, tracked
// as outstanding). Weekly the tenant collects payments — PARTIAL allowed,
// outstanding reduces by (cash + wallet deduction). Optional FILLBACK
// re-extends the paid portion into the wallet so the player keeps playing.
// Full trace lives in the Player Credit Report page.
@Component({
  selector: 'app-player-credit',
  templateUrl: './player-credit.component.html',
  styleUrls: ['./player-credit.component.scss']
})
export class PlayerCreditComponent implements AfterViewInit {
  @ViewChild('search') search;

  public is_searching_player: boolean = false;
  public is_loading: boolean = false;
  public player_search: any;
  public players: any = [];
  public player: any = {};
  public data: any = {};

  public outstanding: number = 0;
  public wallet_balance: any = null;
  public history: any = [];
  public outstanding_list: any = [];
  public grant: any = {};
  public collectData: any = {};

  constructor(private http: HttpService, private route: ActivatedRoute) { }

  ngAfterViewInit(): void {
    this.searchPlayerListener();
    this.loadOutstandingList();
    // Deep-link support: ?player=<uuid> lands with the player pre-selected.
    this.route.queryParams.subscribe((p: any) => {
      if (p && p.player && this.data.player_uuid !== p.player) {
        this.preselectPlayer(p.player);
      }
    });
  }

  searchPlayerListener() {
    try {
      this.search.valueChanges
        .pipe(debounceTime(500))
        .pipe(distinctUntilChanged())
        .subscribe((v: any) => {
          if (!v || v.length < 3) {
            return;
          }
          this.searchPlayer(v);
        });
    } catch (error) { }
  }

  async preselectPlayer(uuid: string) {
    try {
      if (!uuid) { return; }
      let y: any = await this.http.post('/admin/player/get/v2', { player_uuid: uuid });
      if (!y || !y.uuid) { return; }
      this.players = [y];
      await this.selectPlayer(uuid);
    } catch (e) { }
  }

  async searchPlayer(v: any) {
    delete this.data.player_uuid;
    this.is_searching_player = true;
    this.players = await this.http.post('/admin/player/search', { search: v });
    this.is_searching_player = false;
  }

  async selectPlayer(player_uuid) {
    this.player_search = null;
    this.data.player_uuid = player_uuid;
    this.player = this.players.find((v: any) => v.uuid == player_uuid);
    this.grant = {};
    this.collectData = {};
    await this.refresh();
  }

  resetPlayer() {
    this.data = {};
    this.player = {};
    this.players = [];
    this.player_search = null;
    this.history = [];
    this.outstanding = 0;
    this.wallet_balance = null;
    this.loadOutstandingList();
    setTimeout(() => { this.searchPlayerListener(); }, 100);
  }

  async refresh() {
    this.is_loading = true;
    let y: any = await this.http.post('/admin/player-credit/outstanding', { player_uuid: this.data.player_uuid });
    this.outstanding = parseFloat(y.outstanding || 0);
    this.history = y.history || [];

    let w: any = await this.http.post('/admin/player/wallet-balance', { uuid: this.player.uuid, id: this.player.id });
    this.wallet_balance = w.balance;
    this.is_loading = false;
  }

  async loadOutstandingList() {
    this.outstanding_list = await this.http.post('/admin/player-credit/outstanding', {});
  }

  async grantCredit() {
    if (!this.grant.amount || parseFloat(this.grant.amount) <= 0) {
      this.http.notify('Credit amount is required.');
      return;
    }

    if (!confirm('Grant credit ' + parseFloat(this.grant.amount).toFixed(2) + ' to ' + this.player.code + '? The amount will be credited into the player wallet.')) {
      return;
    }

    this.is_loading = true;
    let y: any = await this.http.post('/admin/player-credit/grant', {
      player_uuid: this.data.player_uuid,
      amount: this.grant.amount,
      remark: this.grant.remark,
    });
    this.is_loading = false;

    if (!y.status && y.message) {
      this.http.notify(y.message);
      return;
    }

    this.grant = {};
    await this.refresh();
  }

  async collectCredit() {
    const cash = parseFloat(this.collectData.amount || 0) || 0;
    const deduct = parseFloat(this.collectData.wallet_deduct || 0) || 0;
    const settled = cash + deduct;

    if (settled <= 0) {
      this.http.notify('Enter a cash amount and/or a wallet deduction.');
      return;
    }
    if (settled > this.outstanding) {
      this.http.notify('Settlement ' + settled.toFixed(2) + ' exceeds outstanding credit ' + this.outstanding.toFixed(2) + '.');
      return;
    }
    if (this.collectData.fillback && cash <= 0) {
      this.http.notify('Fillback re-extends the PAID (cash) portion — a collected amount is required.');
      return;
    }

    let msg = 'Collect ' + cash.toFixed(2) + ' cash'
      + (deduct > 0 ? ' + deduct ' + deduct.toFixed(2) + ' from wallet' : '')
      + ' from ' + this.player.code + '.'
      + (this.collectData.fillback
        ? ' Fillback ON: ' + cash.toFixed(2) + ' is credited back to the wallet and stays outstanding.'
        : ' Outstanding will become ' + Math.max(0, this.outstanding - settled).toFixed(2) + '.');
    if (!confirm(msg)) {
      return;
    }

    this.is_loading = true;
    let y: any = await this.http.post('/admin/player-credit/collect', {
      player_uuid: this.data.player_uuid,
      amount: this.collectData.amount,
      wallet_deduct: this.collectData.wallet_deduct,
      fillback: !!this.collectData.fillback,
      remark: this.collectData.remark,
    });
    this.is_loading = false;

    if (y.message) {
      this.http.notify(y.message);
    }
    if (!y.status) {
      return;
    }

    this.collectData = {};
    await this.refresh();
    await this.loadOutstandingList();
  }
}
